/**
 * Posting-rule transport.
 *
 * A posting rule is how a business event becomes journal lines: for one event
 * type, each line names a role, a side, which payload field carries its
 * amount, and where its account comes from — a fixed account, or one resolved
 * through a map (product category, tax class, partner control account).
 *
 * Two contract facts the callers depend on:
 *   • A rule is editable only until it is activated. After that the only
 *     permitted change is deactivation — the lines that priced past journals
 *     must stay exactly as they were when they priced them.
 *   • The dry run resolves an event and answers the lines it WOULD post,
 *     committing nothing. It is the only safe way to ask "what would this do".
 */
import type { components } from "@erp/api-client";

import { apiFetch, type Page } from "@/lib/api/http";
import { fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type PostingRule = Schemas["PostingRule"];
export type PostingRuleCreate = Schemas["PostingRuleCreate"];
export type PostingRulePatch = Schemas["PostingRulePatch"];
export type PostingRuleLineInput = Schemas["PostingRuleLineInput"];
export type AccountSource = Schemas["AccountSource"];
export type PostingRuleSide = Schemas["PostingRuleSide"];
export type DryRunResult = Schemas["PostingRuleDryRunResult"];

const MERGE_PATCH = { "Content-Type": "application/merge-patch+json" } as const;

/** The event types Phase 1 posts from. `manual_journal` is deliberately
 *  absent: it bypasses posting-rule lookup and carries its own lines. */
export const EVENT_TYPES = ["goods_received", "stock_issued", "stock_adjusted"] as const;

export const ACCOUNT_SOURCES: readonly AccountSource[] = [
  "fixed",
  "product_category_map",
  "tax_class_map",
  "partner_control_account",
];

/** The payload key each event type carries its amount in. A rule line naming
 *  any other field resolves to nothing at post time. */
export const AMOUNT_FIELDS: Record<string, string> = {
  goods_received: "inventory_value",
  stock_issued: "cost_amount",
  stock_adjusted: "value_delta",
};

export function listPostingRules(
  query: { event_type?: string; active?: boolean } = {},
  signal?: AbortSignal,
): Promise<Page<PostingRule>> {
  return apiFetch<Page<PostingRule>>("/posting-rules", {
    query: { ...query, limit: 200 },
    signal,
  });
}

export function getPostingRule(postingRuleId: string): Promise<WithEtag<PostingRule>> {
  return fetchWithEtag<PostingRule>(`/posting-rules/${encodeURIComponent(postingRuleId)}`);
}

export function createPostingRule(
  input: PostingRuleCreate,
  idempotencyKey: string,
): Promise<PostingRule> {
  return apiFetch<PostingRule>("/posting-rules", { method: "POST", body: input, idempotencyKey });
}

export function updatePostingRule(
  postingRuleId: string,
  patch: PostingRulePatch,
  ifMatch: string,
): Promise<PostingRule> {
  return apiFetch<PostingRule>(`/posting-rules/${encodeURIComponent(postingRuleId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
}

export function deletePostingRule(postingRuleId: string, ifMatch: string): Promise<void> {
  return apiFetch<void>(`/posting-rules/${encodeURIComponent(postingRuleId)}`, {
    method: "DELETE",
    ifMatch,
  });
}

/** `dryRunPostingRule` — POST · ledger.posting-rule.preview. Commits nothing:
 *  the answer always carries `committed: false`. */
export function dryRunPostingRule(body: {
  event: unknown;
  posting_date: string;
}): Promise<DryRunResult> {
  return apiFetch<DryRunResult>("/posting-rules/dry-run", { method: "POST", body });
}
